import React, { useState, useMemo } from 'react';

import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { CargoTrackingContainer } from '@components/utilities/CargoTracking/CargoTrackingContainer';
import { CargoTrackingHeader } from '@components/utilities/CargoTracking/CargoTrackingHeader';
import {
  TrackingHeader,
  TrackingItem,
} from '@components/utilities/CargoTracking/CargoTrackingResult';
import { useTrackingDataList } from '@hooks/queries/useTrackingDataList';
import { useSearch } from '@hooks/useSearch';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import { TrackingData } from '@models/TrackingData';
import { cargoTrackingResultQuery } from '@pages/utilities/CargoTracking/helpers';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { stringifySearch } from '@utils/types';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '貨物追跡',
    prompt: '追跡結果',
    print: '一覧印刷',
    nThItem: (n: number) => `${n}件目`,
    goBack: '追跡ページへ戻る',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  resultContainer: {
    ...common.flexColumn,
    backgroundColor: theme.palette.background.paper,
    padding: '15px 40px 40px',
  },
  buttonContainer: {
    ...common.flexCenter,
    margin: '44px 0',
  },
  button: {
    padding: '12px 50px',
  },
  tableContainer: {
    background: theme.palette.background.paper,
    padding: '0 1rem',
    boxShadow: 'none',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  table: {
    ...common.bigText,
  },
}));

export const CargoTrackingResult: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [error, setError] = useState('');

  const [expanded, setExpanded] = useState<number | null>(null);

  const {
    search: { trackNos },
  } = useSearch(cargoTrackingResultQuery);

  const trackingList = useTrackingDataList(trackNos);

  const trackNoToData = useMemo(() => {
    const map = new Map<string, TrackingData>();
    if (trackingList.data === undefined) return map;

    for (const data of trackingList.data) {
      map.set(data.track_no, data);
    }
    return map;
  }, [trackingList.data]);

  const onClickPrint = () => {
    const searchString = stringifySearch({ trackNos });
    window.open(
      `${url('/utilities/CargoTracking/PrintCargoTrackingOverview')}?${searchString}`,
      '_blank',
    );
  };

  return (
    <Layout title={strings.title} loading={trackingList.isLoading} errors={[trackingList.error]}>
      {() => (
        <CargoTrackingContainer>
          <Alert open={Boolean(error)} severity="error" onClose={() => setError('')}>
            {error}
          </Alert>

          <div style={{ height: 16 }} />

          <CargoTrackingHeader
            title={strings.prompt}
            buttonTitle={strings.print}
            onClickButton={onClickPrint}
          />

          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table aria-label="collapsible table" classes={{ root: classes.table }}>
              <TrackingHeader />
              <TableBody>
                {trackNos.map((trackNo, i) => {
                  return (
                    <TrackingItem
                      key={i}
                      index={i}
                      expanded={i === expanded}
                      onExpand={() => (i === expanded ? setExpanded(null) : setExpanded(i))}
                      trackingData={trackNoToData.get(trackNo)!}
                      trackNo={trackNo}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <div className={classes.buttonContainer}>
            <Link to={url('/utilities/CargoTracking/CargoTracking')}>
              <Button
                color="primary"
                text={strings.goBack}
                className={clsx(classes.biggerTextImportant, classes.button)}
              />
            </Link>
          </div>
        </CargoTrackingContainer>
      )}
    </Layout>
  );
};
